/**
 * API utilities for fetching menu data
 * Implements Proxy-based state management with validation
 */

// API base URL
const API_BASE = '/api/v1/public/menu';

// Allowed state keys for proxy validation
const ALLOWED_KEYS = new Set(['lang', 'currency', 'theme', 'fontSize']);

/**
 * Fetch menu data
 * @returns {Promise<Object>} Menu data
 */
export async function fetchMenuData() {
  const response = await fetch(API_BASE, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Menu not found');
    }
    throw new Error(`Failed to load menu: ${response.status}`);
  }

  const data = await response.json();
  
  if (!data.success) {
    throw new Error(data.message || 'Failed to load menu');
  }

  return data.data;
}

/**
 * Initialize global APP_DATA with Proxy state
 * Returns { content: frozenData, state: proxy }
 */
export function initAppData() {
  const dataPromise = fetchMenuData().then(hugePayload => {
    // Freeze the API data to prevent mutations
    const frozenContent = Object.freeze(hugePayload);
    
    const internalState = {};
    const pendingChanges = new Map();
    let flushScheduled = false;
    
    // Get the event bus
    const bus = globalThis[Symbol.for('APP_BUS')];
    
    const flush = () => {
      // Dispatch individual change events
      for (const [key, value] of pendingChanges) {
        bus.dispatchEvent(
          new CustomEvent(`change:${key}`, { detail: value })
        );
      }
      
      // Dispatch batch event with all changes
      bus.dispatchEvent(
        new CustomEvent('change:batch', { detail: Object.fromEntries(pendingChanges) })
      );
      
      pendingChanges.clear();
      flushScheduled = false;
    };
    
    const stateProxy = new Proxy(internalState, {
      set(target, prop, value) {
        const key = String(prop);
        
        // Validate key is allowed
        if (!ALLOWED_KEYS.has(key)) {
          console.warn(`[State] Invalid key: ${key}. Allowed: ${Array.from(ALLOWED_KEYS).join(', ')}`);
          return false;
        }
        
        // Skip if value hasn't changed
        if (target[key] === value) return true;
        
        target[key] = value;
        pendingChanges.set(key, value);
        
        // Schedule flush only once per sync tick
        if (!flushScheduled) {
          flushScheduled = true;
          queueMicrotask(flush);
        }
        
        return true;
      },
      
      get(target, prop) {
        return target[prop];
      }
    });
    
    return { content: frozenContent, state: stateProxy };
  });
  
  globalThis[Symbol.for('APP_DATA')] = dataPromise;
  
  return dataPromise;
}

/**
 * Retry wrapper for fetch with exponential backoff
 */
export async function fetchWithRetry(url, options = {}, maxRetries = 3) {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await fetch(url, options);
      if (response.ok) return response;
      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      lastError = error;
      const delay = Math.pow(2, i) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}
